import { inject, provideAppInitializer } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AuthStore } from '@root/app/store/auth.store';

/**
 * Resolve the auth session before the first route renders so guards never see an
 * indeterminate state and the login page cannot flash (BUG 1).
 *
 * MSAL v4 (@azure/msal-browser) REQUIRES `await instance.initialize()` before any
 * other MSAL API call. MsalGuard.canActivate initializes lazily, so interactive
 * login works — but on a refresh with an expired token the AuthStore permissions
 * effect fires an API request before any guard runs. AuthorizeInterceptor then
 * calls msalService.instance.getAccountByLocalId() (refreshViaMsal), which throws
 * `uninitialized_public_client_application` synchronously, escaping the pipe's
 * catchError -> uncaught error, blank page, no redirect to login. Awaiting
 * initialize() here (an app initializer, so bootstrap blocks on it) guarantees the
 * instance is ready before that effect — or anything else — touches MSAL.
 *
 * AuthStore's onInit hook restores the localStorage token SYNCHRONOUSLY the moment
 * the store is injected here. For a normal (non-MSAL-callback) load we can mark the
 * store ready immediately. For the MSAL redirect callback we leave authReady=false
 * and let AppComponent flip it once MSAL settles (InteractionStatus.None) — which
 * always happens, so the app can never deadlock.
 */
export const provideAuthInitializer = () =>
  provideAppInitializer(async () => {
    // inject() needs a SYNCHRONOUS injection context — grab both refs before any await.
    const msalInstance = inject<IPublicClientApplication>(MSAL_INSTANCE);
    const authStore = inject(AuthStore); // triggers onInit -> localStorage restore (sync)

    // Bootstrap blocks on this promise, so MSAL is ready before the first CD tick runs
    // AuthStore's permissions effect (and thus before AuthorizeInterceptor.refreshViaMsal).
    await msalInstance.initialize();

    if (!isMsalRedirectCallback()) {
      authStore.markReady();
    }
  });

function isMsalRedirectCallback(): boolean {
  const path = globalThis.location.pathname;
  const hash = globalThis.location.hash;
  return (
    path.includes('auth-callback') ||
    path.startsWith('/code') ||
    hash.includes('code=') ||
    hash.includes('error=')
  );
}
